// ============================================================
// TMR Portal Admin — a searchable modal for picking one record from a backend directory
// ============================================================
import { useState } from 'react';
import type { MouseEvent, ReactNode } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { IconTile } from './primitives';
import { SearchBox } from './ui';
import { EmptyState, ErrorState, Skeleton } from './states';
import { Icons, StrokeIcon } from '../lib/icons';
import { useDebounced } from '../useDebounced';
import { OverlayPortal } from './OverlayPortal';

/** How a picked record is shown in a result row — a heading and a muted line under it. */
export interface RecordRow {
  primary: ReactNode;
  secondary: ReactNode;
}

/** Props for {@link RecordPicker}. */
export interface RecordPickerProps<T> {
  /** Eyebrow over the title, e.g. "Gestor da conta". */
  eyebrow: string;
  /** Modal heading. */
  title: string;
  /** One line under the heading explaining what a pick does. */
  subtitle: string;
  placeholder: string;
  /**
   * The search hook the picker drives with its debounced term. Named with a `use` prefix so it is a hook
   * the linter recognizes — every caller passes one of the `data/` search hooks.
   */
  useSearch: (term: string) => UseQueryResult<T[]>;
  /** Stable key for a result, for React's list reconciliation. */
  getKey: (item: T) => string;
  /** Projects a result into the two lines a row shows. */
  toRow: (item: T) => RecordRow;
  /** Icon for a result row; defaults to the people glyph. */
  rowIcon?: ReactNode;
  /** What to search by, shown before the operator types. Defaults to the people wording. */
  idleMessage?: string;
  /** Said when the search returned nothing. Defaults to the people wording. */
  notFoundMessage?: string;
  /** Called with the chosen record; the picker closes right after. */
  onSelect: (item: T) => void;
  onClose: () => void;
}

/** Below this many characters a search is not worth a round-trip, so the picker stays idle. */
const MIN_SEARCH_LENGTH = 2;

/**
 * Picks one record from a directory the backend already scopes to what the operator may see (06 §6).
 *
 * It is generic on purpose: the Gestor da Conta picker reads Users, the access-approval scope picker reads
 * Empresas and Holdings, but the shape — search, the four states, pick a row — is identical, so it is one
 * component rather than three (skill `frontend-architecture`, Regra 1, one level down). The caller owns
 * both what a pick does and the copy, since "Ninguém encontrado" is wrong for an Empresa.
 *
 * The picker never renders where the operator lacks the directory's permission — the caller gates it —
 * because a 403 on every search is a worse experience than not offering the search at all.
 */
export function RecordPicker<T>({
  eyebrow,
  title,
  subtitle,
  placeholder,
  useSearch,
  getKey,
  toRow,
  rowIcon = Icons.users,
  idleMessage = 'Busque por nome, e-mail ou CPF.',
  notFoundMessage = 'Ninguém encontrado.',
  onSelect,
  onClose,
}: RecordPickerProps<T>) {
  const [search, setSearch] = useState('');
  const debounced = useDebounced(search);
  const results = useSearch(debounced);
  const stop = (e: MouseEvent) => e.stopPropagation();

  /**
   * Dismissing the picker must not reach whatever is behind it. This picker is often opened from inside
   * another modal whose own backdrop closes it, and the click that dismisses the picker would otherwise
   * bubble straight into it — taking the operator's half-filled form with it.
   */
  const dismiss = (e: MouseEvent) => {
    e.stopPropagation();
    onClose();
  };

  return (
    <OverlayPortal>
      <div
        onClick={dismiss}
        style={{ position: 'fixed', inset: 0, zIndex: 90, background: 'rgba(28,30,28,0.32)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
      >
        <div
          onClick={stop}
          style={{ width: 560, maxWidth: '100%', maxHeight: '82vh', background: 'var(--cream-50)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        >
          <div style={{ padding: '20px 26px', borderBottom: '1px solid var(--border-default)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
            <div>
              <div className="pa-eyebrow" style={{ marginBottom: 4 }}>{eyebrow}</div>
              <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: 18, margin: 0, letterSpacing: '-0.01em' }}>{title}</h3>
              <p style={{ margin: '6px 0 0', fontSize: 12.5, color: 'var(--fg-muted)', maxWidth: 420, lineHeight: 1.5 }}>{subtitle}</p>
            </div>
            <button type="button" onClick={onClose} className="pa-icon-btn" style={{ width: 34, height: 34, flexShrink: 0 }}>
              <StrokeIcon d={Icons.x} size={18} />
            </button>
          </div>

          <div style={{ padding: '18px 26px 0' }}>
            <SearchBox value={search} onChange={setSearch} placeholder={placeholder} width="100%" />
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '16px 26px 26px', minHeight: 200 }}>
            <PickerResults
              term={debounced}
              results={results}
              getKey={getKey}
              toRow={toRow}
              rowIcon={rowIcon}
              idleMessage={idleMessage}
              notFoundMessage={notFoundMessage}
              onPick={(item) => {
                onSelect(item);
                onClose();
              }}
            />
          </div>
        </div>
      </div>
    </OverlayPortal>
  );
}

/** Props for {@link PickerResults}. */
interface PickerResultsProps<T> {
  term: string;
  results: UseQueryResult<T[]>;
  getKey: (item: T) => string;
  toRow: (item: T) => RecordRow;
  rowIcon: ReactNode;
  idleMessage: string;
  notFoundMessage: string;
  onPick: (item: T) => void;
}

/** The result rows, and the four states a search can be in (skill `frontend-architecture`, Regra 7). */
function PickerResults<T>({ term, results, getKey, toRow, rowIcon, idleMessage, notFoundMessage, onPick }: PickerResultsProps<T>) {
  if (term.trim().length < MIN_SEARCH_LENGTH) {
    return <EmptyState message={idleMessage} hint={`Digite ao menos ${MIN_SEARCH_LENGTH} caracteres.`} />;
  }
  if (results.isPending) return <Skeleton rows={4} />;
  if (results.isError) return <ErrorState error={results.error} onRetry={() => void results.refetch()} />;
  if (results.data.length === 0) {
    return <EmptyState message={notFoundMessage} hint="Revise o termo buscado." />;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {results.data.map((item) => {
        const row = toRow(item);
        return (
          <button
            key={getKey(item)}
            type="button"
            onClick={() => onPick(item)}
            className="pa-row"
            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '11px 12px', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', background: 'var(--cream-50)', cursor: 'pointer', textAlign: 'left' }}
          >
            <IconTile size={34}>
              <StrokeIcon d={rowIcon} size={16} />
            </IconTile>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.primary}</div>
              <div style={{ fontSize: 12.5, color: 'var(--fg-subtle)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.secondary}</div>
            </div>
            <span style={{ color: 'var(--stone-400)', display: 'flex' }}>
              <StrokeIcon d={Icons.chevronR} size={16} />
            </span>
          </button>
        );
      })}
    </div>
  );
}
